import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { smoothScrollTo } from "./shared";

const HERO_IMG =
  "https://images.unsplash.com/photo-1532170579297-281918c8ae72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxtb29keSUyMGRhcmslMjBzdHVkaW8lMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODQwODEzNTB8MA&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = ["DIGITAL MEDIA", "PERFORMERS &", "MODELS WANTED"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden"
    >
      <motion.div
        style={{ y: imgY, scale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_IMG}
          alt="Moody studio portrait"
          className="h-full w-full object-cover object-center grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-black/40" />
        <div className="absolute inset-0 bg-black/40" />
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-[#d4af37] text-xs tracking-[0.4em] uppercase mb-6"
        >
          Boutique Digital Media Production
        </motion.p>

        <h1 className="font-display text-white font-extrabold tracking-tighter uppercase text-4xl sm:text-6xl lg:text-8xl leading-[0.95]">
          {HEADLINE.map((text, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-8 max-w-xl text-[#cccccc] text-base sm:text-lg leading-relaxed"
        >
          We are a boutique production agency specializing in collaborating with
          independent content creators, performers, and creative duos for a
          premium, subscription-based internet media environment.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex items-center gap-6"
        >
          <button
            data-testid="hero-apply-btn"
            onClick={() => smoothScrollTo("apply")}
            className="group font-display font-bold text-sm tracking-[0.2em] uppercase bg-[#d4af37] text-black px-8 py-4 hover:bg-[#e5a93b] transition-all duration-300 hover:scale-[1.03] active:scale-95 flex items-center gap-3"
          >
            Apply Below
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
